const RTL_CHARACTER = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const LETTER = /\p{L}/u;
const LINE_TAGS = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6"]);
const DIRECTED_BLOCKS = "p, h1, h2, h3, h4, h5, h6, blockquote";

export const startsWithRtl = (text) => {
  for (const char of text || "") {
    if (RTL_CHARACTER.test(char)) return true;
    if (LETTER.test(char)) return false;
  }
  return false;
};

const escapeHtml = (text) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const parseBody = (html) =>
  new DOMParser().parseFromString(html || "", "text/html").body;

export const plainTextToHtml = (text) =>
  (text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => {
      if (!line.trim()) return "<p><br></p>";
      const direction = startsWithRtl(line) ? ' dir="rtl"' : "";
      return `<p${direction}>${escapeHtml(line)}</p>`;
    })
    .join("");

const renderChildren = (node) =>
  Array.from(node.childNodes).map(renderNode).join("");

const renderList = (list) => {
  const ordered = list.tagName === "OL";
  return Array.from(list.children)
    .filter((child) => child.tagName === "LI")
    .map((item, index) => {
      const marker = ordered ? `${index + 1}. ` : "- ";
      return `${marker}${renderChildren(item).trim()}\n`;
    })
    .join("");
};

const renderLink = (link) => {
  const label = renderChildren(link);
  const href = link.getAttribute("href") || "";
  const target = href.replace(/^mailto:/i, "");
  const shown = label.trim();
  if (!target || shown === href || shown === target) return label;
  return `${label} (${href})`;
};

const renderQuote = (quote) =>
  renderChildren(quote)
    .replace(/\n+$/, "")
    .split("\n")
    .map((line) => `> ${line}`.trimEnd())
    .join("\n") + "\n";

function renderNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent.replace(/\u00A0/g, " ");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  switch (node.tagName) {
    case "BR":
      return node.nextSibling ? "\n" : "";
    case "UL":
    case "OL":
      return renderList(node);
    case "A":
      return renderLink(node);
    case "BLOCKQUOTE":
      return renderQuote(node);
    case "SCRIPT":
    case "STYLE":
      return "";
    default:
      return LINE_TAGS.has(node.tagName)
        ? `${renderChildren(node)}\n`
        : renderChildren(node);
  }
}

export const htmlToPlainText = (html) =>
  renderChildren(parseBody(html)).replace(/^\n+/, "").replace(/\s+$/, "");

export const isEmailBodyBlank = (html) => htmlToPlainText(html).trim() === "";

const quoteStyle = (rtl) => {
  const side = rtl ? "right" : "left";
  return `margin:0;padding-${side}:1em;border-${side}:4px solid #ccc`;
};

export const finalizeEmailHtml = (html) => {
  const body = parseBody(html);

  body.querySelectorAll(".ql-direction-rtl").forEach((element) => {
    element.setAttribute("dir", "rtl");
  });

  body.querySelectorAll(DIRECTED_BLOCKS).forEach((block) => {
    if (!block.hasAttribute("dir") && startsWithRtl(block.textContent)) {
      block.setAttribute("dir", "rtl");
    }
  });

  body.querySelectorAll("ul, ol").forEach((list) => {
    const firstItem = list.firstElementChild;
    const rtl =
      firstItem?.getAttribute("dir") === "rtl" ||
      startsWithRtl(list.textContent);
    if (rtl) list.setAttribute("dir", "rtl");
  });

  body.querySelectorAll("p").forEach((paragraph) => {
    paragraph.setAttribute("style", "margin:0");
  });

  body.querySelectorAll("blockquote").forEach((quote) => {
    quote.setAttribute("style", quoteStyle(quote.getAttribute("dir") === "rtl"));
  });

  body.querySelectorAll("[class]").forEach((element) => {
    element.removeAttribute("class");
  });

  return body.innerHTML;
};
